import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Upload, X as XIcon, FileText } from 'lucide-react';
import TextInput from '../../components/ui/TextInput.jsx';
import CheckboxInput from '../../components/ui/CheckboxInput';
import Button from '../../components/ui/Button';
import ConsentModal from './ConsentModal';
import { submitApplication } from '../../services/careerService';

const Card = styled.section`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 10px;
  padding: 24px 28px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FullRow = styled.div`
  margin-bottom: 14px;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1.5px dashed #D5DCE3;
  border-radius: 8px;
  padding: 12px 14px;
  background: #FAFBFC;
  cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.primary.main}; }
`;

const FileLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-size: 13px;
  flex: 1;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.dark.little};
  cursor: pointer;
  padding: 4px;
  &:hover { color: ${({ theme }) => theme.colors.auxiliar.danger}; }
`;

// FE-36 — TEK GENEL HATA SATIRI. Eski hâlde form BİRİNCİ kusurda duruyordu:
// adı boş + CV'si yok + onayı işaretsiz bir aday üç kez gönderip üç ayrı
// mesaj görüyordu ("Ad Soyad ve E-posta zorunludur" → "CV yüklemeniz zorunlu"
// → "aydınlatma metnini onaylamanız gerekiyor"). Üstelik ilk mesaj HANGİ
// alanın eksik olduğunu söylemiyordu. Artık tüm kusurlar TEK seferde bulunur,
// her biri KENDİ alanının altında yazar; bu satır yalnız özet + sunucu hatası
// taşır ve `role="alert"` olduğu için ekran okuyucuya da ulaşır.
const Error = styled.p`
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  font-size: 13px;
  margin: 12px 0 0 0;
`;

// Onay kutusunun kendi hata satırı: `CheckboxInput` `error` prop'u kabul etmiyor
// (paylaşılan bileşen — ÖLÇÜLDÜ: 43 dosyada 85 kullanım; bu partide dokunulmadı).
const FieldError = styled.span`
  display: block;
  margin-top: 6px;
  margin-left: 30px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

// PARTİ 19 — SARMALAYICI KALDIRILDI. Klavye erişimi artık `CheckboxInput`'un
// KENDİSİNDE (gerçek, görünmez bir native checkbox): ürünün 42 dosyadaki 86
// onay kutusunun hepsi birden düzeldi. Buradaki `role="checkbox"` + `tabIndex`
// sarmalayıcısı korunsaydı aynı kontrol İKİ KEZ duyurulur ve sekme sırasında
// İKİ durak olurdu (iç içe checkbox rolü) — yani çözüm kendi kusurunu
// doğururdu. `consentRef` artık native input'a bağlanır; odaklama ve
// `aria-invalid` onun üstünde.

const Actions = styled.div`
  margin-top: 18px;
  display: flex;
  justify-content: flex-end;
`;

const ConsentLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 6px 0 0 30px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.primary.main};
  text-decoration: underline;
  cursor: pointer;
  font-family: ${({ theme }) => theme.metrics.font.family};
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

// Honeypot (BP-11): insan kullanıcı görmez (ekran dışı, sekme sırası dışı, ekran
// okuyucuya kapalı); form-doldurucu botlar "website" adlı alanı doldurur. Sunucu
// alan doluysa sessizce "gönderildi" der ve HİÇBİR ŞEY kaydetmez. display:none
// KULLANILMAZ — bazı botlar görünmez alanları atlar; ekran dışı konumlandırma
// tarayıcı açısından "görünür" bir alandır.
const HoneypotField = styled.div`
  position: absolute;
  left: -10000px;
  height: 0;
  overflow: hidden;
`;

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIMES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
// Sunucu tavanlarıyla (CareerDataAccess) birebir; aşımı sunucu `career.fieldTooLong` ile reddeder.
const MAX_LEN = { fullName: 200, email: 254, phone: 32, linkedInUrl: 300, coverLetter: 5000 };

// Sunucu tavanıyla aynı kaba biçim denetimi. Amaç RFC uyumu değil, adayın
// "ad@sirket" gibi bir yazım hatasıyla başvurusunu kaybetmemesi: e-posta
// süreçteki TEK iletişim kanalıdır, yanlışsa aday bir daha haber alamaz.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// `invitationToken` (SPEC F2): davet bağlantısıyla gelindiyse (`?inv=`) başvuru
// gövdesine taşınır; sunucu eşleşirse kaynağı `talent_pool` yapar ve daveti kapatır.
// Geçersiz/eski token başvuruyu BOZMAZ (sunucu yok sayar).
export const buildApplyPayload = (form, invitationToken) => (
  invitationToken ? { ...form, invitationToken } : form
);

export default function CareerApplyForm({
  tenantSlug, jobSlug, jobTitle, companyName, onSuccess, invitationToken = null,
}) {
  const { t } = useTranslation();
  const fileRef = useRef();
  const formRef = useRef(null);
  const consentRef = useRef(null);
  // Gizli `<input type="file">` odaklanamaz (display:none) — odağın gidebileceği
  // gerçek eleman "CV Yükle" düğmesidir.
  const uploadBtnRef = useRef(null);
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', linkedInUrl: '', coverLetter: '', consentAccepted: false,
    website: '', // honeypot — bkz. HoneypotField; payload'a olduğu gibi gider
  });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [consentOpen, setConsentOpen] = useState(false);

  // Alanı düzeltir düzeltmez o alanın hatası kalkar — kullanıcı düzeltmesinin
  // işe yaradığını GÖNDERMEDEN önce görmeli.
  const clearFieldError = (k) => setFieldErrors((prev) => (
    prev[k] ? { ...prev, [k]: undefined } : prev
  ));

  const setField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    clearFieldError(k);
  };

  // Onayın form alanı adı (`consentAccepted`) ile hata anahtarı (`consent`)
  // farklı — `setField` kullanılırsa hata satırı işaretlendikten sonra ASILI
  // KALIRDI (aday onayı verir, kırmızı yazı durur).
  const setConsent = (v) => {
    setForm((prev) => ({ ...prev, consentAccepted: v }));
    clearFieldError('consent');
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Dosya kusurları artık genel hata satırını GASP ETMİYOR: mesaj CV
    // satırının altında, seçicinin yanında duruyor (eskiden formun en altında
    // beliriyor ve bir sonraki "Gönder" ile sessizce siliniyordu).
    if (f.size > MAX_FILE_BYTES) {
      setFieldErrors((prev) => ({ ...prev, resume: t('career.error.resumeTooLarge') }));
      return;
    }
    if (!ALLOWED_MIMES.includes(f.type)) {
      setFieldErrors((prev) => ({ ...prev, resume: t('career.error.resumeUnsupportedType') }));
      return;
    }
    clearFieldError('resume');
    setResume(f);
  };

  // TÜM kusurları tek seferde toplar — ilk kusurda DURMAZ (FE-36'nın çekirdeği).
  const collectErrors = () => {
    const next = {};
    if (!form.fullName.trim()) next.fullName = t('career.error.fullNameRequired');
    if (!form.email.trim()) next.email = t('career.error.emailRequired');
    else if (!EMAIL_SHAPE.test(form.email.trim())) next.email = t('career.error.emailInvalid');
    if (!resume) next.resume = t('career.error.resumeRequired');
    if (!form.consentAccepted) next.consent = t('career.error.consentRequired');
    return next;
  };

  // Odağı İLK kusurlu alana taşır. Uzun bir formda hata satırını yazmak
  // yetmez: mobilde aday hatayı hiç göremeden sayfanın ortasında kalır.
  const focusFirstError = (errors) => {
    const order = ['fullName', 'email', 'resume', 'consent'];
    const first = order.find((k) => errors[k]);
    if (!first) return;
    if (first === 'consent') { consentRef.current?.focus(); return; }
    if (first === 'resume') { uploadBtnRef.current?.focus?.(); return; }
    const el = formRef.current?.querySelector(`[name="${first}"]`);
    if (el && typeof el.focus === 'function') el.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const errors = collectErrors();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError(t('career.error.fixHighlighted'));
      focusFirstError(errors);
      return;
    }

    setFieldErrors({});
    setSubmitting(true);
    try {
      const res = await submitApplication(tenantSlug, jobSlug, buildApplyPayload(form, invitationToken), resume);
      if (res.data?.status) {
        onSuccess?.();
      } else {
        setError(t(`responseErrors.${res.data?.message}`, { defaultValue: t('career.error.generic') }));
      }
    } catch (err) {
      const msg = err.response?.data?.message;
      setError(msg ? t(`responseErrors.${msg}`, { defaultValue: t('career.error.generic') }) : t('career.error.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <SectionTitle>{t('career.applyTo', { title: jobTitle })}</SectionTitle>
      <form onSubmit={handleSubmit} ref={formRef} noValidate>
        <HoneypotField aria-hidden="true">
          <input
            type="text"
            name="website"
            value={form.website}
            onChange={(e) => setField('website', e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </HoneypotField>
        <Row>
          <TextInput
            label={t('career.field.fullName')}
            name="fullName"
            autoComplete="name"
            value={form.fullName}
            onChange={(e) => setField('fullName', e.target.value)}
            maxLength={MAX_LEN.fullName}
            required
            error={fieldErrors.fullName}
            aria-invalid={fieldErrors.fullName ? 'true' : undefined}
          />
          <TextInput
            label={t('career.field.email')}
            type="email"
            name="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => setField('email', e.target.value)}
            maxLength={MAX_LEN.email}
            required
            error={fieldErrors.email}
            aria-invalid={fieldErrors.email ? 'true' : undefined}
          />
        </Row>
        <Row>
          <TextInput
            label={t('career.field.phone')}
            name="phone"
            autoComplete="tel"
            value={form.phone}
            onChange={(e) => setField('phone', e.target.value)}
            maxLength={MAX_LEN.phone}
          />
          <TextInput
            label={t('career.field.linkedIn')}
            name="linkedInUrl"
            autoComplete="url"
            value={form.linkedInUrl}
            onChange={(e) => setField('linkedInUrl', e.target.value)}
            maxLength={MAX_LEN.linkedInUrl}
          />
        </Row>
        <FullRow>
          <TextInput
            label={t('career.field.coverLetter')}
            type="textarea"
            name="coverLetter"
            rows={5}
            value={form.coverLetter}
            onChange={(e) => setField('coverLetter', e.target.value)}
            maxLength={MAX_LEN.coverLetter}
          />
        </FullRow>
        <FullRow>
          <FileRow onClick={() => fileRef.current?.click()}>
            <Button ref={uploadBtnRef} type="button" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}>
              <Upload size={14} />{t('career.field.uploadResume')}
            </Button>
            <FileLabel>
              {resume ? resume.name : t('career.field.resumeHint')}
            </FileLabel>
            {resume && (
              <RemoveBtn type="button" onClick={(e) => { e.stopPropagation(); setResume(null); }}>
                <XIcon size={16} />
              </RemoveBtn>
            )}
            <input
              ref={fileRef}
              type="file"
              name="resume"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleFile}
              style={{ display: 'none' }}
            />
          </FileRow>
          {fieldErrors.resume && (
            <FieldError style={{ marginLeft: 0 }} data-testid="error-resume">{fieldErrors.resume}</FieldError>
          )}
        </FullRow>
        <FullRow>
          <CheckboxInput
            label={t('career.field.consent')}
            checked={form.consentAccepted}
            onChange={setConsent}
            ref={consentRef}
            aria-invalid={fieldErrors.consent ? 'true' : undefined}
            data-testid="consent-checkbox"
          />
          <ConsentLink type="button" onClick={() => setConsentOpen(true)}>
            <FileText size={14} />{t('career.field.consentView')}
          </ConsentLink>
          {fieldErrors.consent && <FieldError data-testid="error-consent">{fieldErrors.consent}</FieldError>}
        </FullRow>

        {error && <Error role="alert" data-testid="form-error">{error}</Error>}

        <Actions>
          <Button type="submit" loading={submitting} disabled={submitting}>
            {t('career.submit')}
          </Button>
        </Actions>
      </form>

      {consentOpen && (
        <ConsentModal companyName={companyName} tenantSlug={tenantSlug} onClose={() => setConsentOpen(false)} />
      )}
    </Card>
  );
}
